export type FlexibleNoteFormat = 'SOAP' | 'DAP' | 'BIRP'

const FLEXIBLE_NOTE_FORMATS: FlexibleNoteFormat[] = ['SOAP', 'DAP', 'BIRP']

export const FLEXIBLE_NOTE_FIELD_SEPARATOR = '\n\n'

const FLEXIBLE_NOTE_FORMAT_FIELDS: Record<FlexibleNoteFormat, string[]> = {
  SOAP: ['soapSubjective', 'soapObjective', 'soapAssessment', 'soapPlan'],
  DAP: ['dapData', 'dapAssessment', 'dapPlan'],
  BIRP: ['birpBehavior', 'birpIntervention', 'birpResponse', 'birpPlan'],
}

export const ALL_FLEXIBLE_NOTE_FIELDS: string[] = FLEXIBLE_NOTE_FORMATS.flatMap(
  (format) => FLEXIBLE_NOTE_FORMAT_FIELDS[format],
)

const CROSS_FORMAT_FIELD_SOURCES: Record<
  FlexibleNoteFormat,
  Partial<Record<FlexibleNoteFormat, Record<string, string[]>>>
> = {
  SOAP: {
    DAP: {
      dapData: ['soapSubjective', 'soapObjective'],
      dapAssessment: ['soapAssessment'],
      dapPlan: ['soapPlan'],
    },
    BIRP: {
      birpBehavior: ['soapSubjective', 'soapObjective'],
      birpIntervention: [],
      birpResponse: ['soapAssessment'],
      birpPlan: ['soapPlan'],
    },
  },
  DAP: {
    SOAP: {
      soapSubjective: ['dapData'],
      soapObjective: [],
      soapAssessment: ['dapAssessment'],
      soapPlan: ['dapPlan'],
    },
    BIRP: {
      birpBehavior: ['dapData'],
      birpIntervention: [],
      birpResponse: ['dapAssessment'],
      birpPlan: ['dapPlan'],
    },
  },
  BIRP: {
    SOAP: {
      soapSubjective: [],
      soapObjective: ['birpBehavior'],
      soapAssessment: ['birpIntervention', 'birpResponse'],
      soapPlan: ['birpPlan'],
    },
    DAP: {
      dapData: ['birpBehavior'],
      dapAssessment: ['birpIntervention', 'birpResponse'],
      dapPlan: ['birpPlan'],
    },
  },
}

export const isFlexibleNoteFormat = (value: unknown): value is FlexibleNoteFormat =>
  typeof value === 'string' && (FLEXIBLE_NOTE_FORMATS as string[]).includes(value)

const asText = (value: unknown): string => (typeof value === 'string' ? value : '')

export const getMappedFlexibleNoteValues = (
  previousBody: Record<string, unknown> | undefined | null,
  targetFormat: FlexibleNoteFormat,
): Record<string, string> => {
  const targetFields = FLEXIBLE_NOTE_FORMAT_FIELDS[targetFormat]
  const result: Record<string, string> = {}
  if (!previousBody) {
    return result
  }

  const sourceFormat = previousBody.noteFormat

  if (!isFlexibleNoteFormat(sourceFormat) || sourceFormat === targetFormat) {
    targetFields.forEach((field) => {
      result[field] = asText(previousBody[field])
    })
    return result
  }

  const fieldSources = CROSS_FORMAT_FIELD_SOURCES[sourceFormat]?.[targetFormat] ?? {}
  targetFields.forEach((field) => {
    const sources = fieldSources[field] ?? []
    result[field] = sources
      .map((sourceField) => asText(previousBody[sourceField]).trim())
      .filter((text) => text.length > 0)
      .join(FLEXIBLE_NOTE_FIELD_SEPARATOR)
  })
  return result
}
